import { useRef, useState } from "react";
import { toast } from "sonner";
import { Type, Upload, Loader2, X, Save, Trash2, Image as ImageIcon } from "lucide-react";
import { api } from "@/lib/api";

const FONTS = [
  { id: "serif", label: "Serif", css: "Georgia, 'Times New Roman', serif" },
  { id: "sans", label: "Sans", css: "Arial, 'Helvetica Neue', sans-serif" },
  { id: "mono", label: "Mono", css: "'Courier New', monospace" },
];
const PALETTES = [
  { bg: "#C9A24B", fg: "#0A0A0A" },
  { bg: "#0A0A0A", fg: "#E5C378" },
  { bg: "#22C55E", fg: "#FFFFFF" },
  { bg: "#EF4444", fg: "#FFFFFF" },
  { bg: "#1E6FB8", fg: "#FFFFFF" },
  { bg: "#B6FF3C", fg: "#0A0A0A" },
];

export default function LogoEditor({ siteId, site, onClose, onChange }) {
  const initial = site.logo || {};
  const [tab, setTab] = useState(initial.kind === "upload" ? "upload" : "text");
  const [initials, setInitials] = useState(initial.initials || (site.business_name?.[0] || "W").toUpperCase());
  const [bg, setBg] = useState(initial.bg || "#C9A24B");
  const [fg, setFg] = useState(initial.fg || "#0A0A0A");
  const [font, setFont] = useState(initial.font || "serif");
  const [saving, setSaving] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileRef = useRef(null);
  const fontCss = FONTS.find((f) => f.id === font)?.css;

  const saveText = async () => {
    const trimmed = (initials || "").trim().toUpperCase().slice(0, 3);
    if (!trimmed) return toast.error("Add 1-3 initials");
    setSaving(true);
    try {
      const { data } = await api.patch(`/sites/${siteId}/logo/text`,
        { initials: trimmed, bg, fg, font });
      onChange?.(data.site);
      toast.success("Logo saved");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const pickFile = () => fileRef.current?.click();

  const uploadFile = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      return toast.error("Only PNG, JPG or WebP allowed");
    }
    if (file.size > 500 * 1024) return toast.error("Image is too large — keep it under 500 KB");
    setUploadPreview(URL.createObjectURL(file));
    setSaving(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/sites/${siteId}/logo/upload`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(data.site);
      toast.success("Logo uploaded");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not upload"); setUploadPreview(null); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!site.logo) return onClose();
    if (!window.confirm("Remove the logo from this website?")) return;
    setSaving(true);
    try {
      const { data } = await api.delete(`/sites/${siteId}/logo`);
      onChange?.(data.site);
      toast.success("Logo removed");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" data-testid="logo-editor-sheet" onClick={onClose}>
      <div className="bg-[#161616] border-l border-[#27272A] w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-[#C9A24B]" /> Website Logo</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-5">Design a text logo instantly, or upload your existing one.</p>

        <div className="inline-flex rounded-xl border border-[#27272A] p-1 bg-[#0A0A0A] mb-5">
          <button data-testid="logo-tab-text" onClick={() => setTab("text")}
            className={`px-4 py-2 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 ${tab === "text" ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-400"}`}>
            <Type className="w-3.5 h-3.5" /> Text Logo
          </button>
          <button data-testid="logo-tab-upload" onClick={() => setTab("upload")}
            className={`px-4 py-2 text-xs font-semibold rounded-lg inline-flex items-center gap-1.5 ${tab === "upload" ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-400"}`}>
            <Upload className="w-3.5 h-3.5" /> Upload
          </button>
        </div>

        {tab === "text" ? (
          <>
            {/* Preview */}
            <div className="rounded-2xl border border-[#27272A] bg-[#0A0A0A] p-8 flex justify-center mb-5">
              <div style={{ background: bg, color: fg, fontFamily: fontCss, width: 88, height: 88, borderRadius: 20,
                display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 36 }}>
                {initials || "?"}
              </div>
            </div>

            <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Initials (1-3)</label>
            <input data-testid="logo-text-initials" value={initials}
              onChange={(e) => setInitials(e.target.value.toUpperCase().slice(0, 3))} maxLength={3}
              className="mt-2 w-full bg-[#0A0A0A] border border-[#27272A] rounded-xl p-3 text-lg text-zinc-100 uppercase focus:outline-none focus:border-[#C9A24B]" />

            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Font</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {FONTS.map((f) => (
                  <button key={f.id} data-testid={`logo-font-${f.id}`} onClick={() => setFont(f.id)}
                    className={`py-2 rounded-lg border text-sm transition-all ${font === f.id ? "border-[#C9A24B] bg-[#C9A24B]/10 text-[#E5C378]" : "border-[#27272A] text-zinc-300"}`}
                    style={{ fontFamily: f.css }}>{f.label}</button>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">Palette</label>
              <div className="grid grid-cols-6 gap-2 mt-2">
                {PALETTES.map((p, i) => (
                  <button key={i} data-testid={`logo-palette-${i}`} onClick={() => { setBg(p.bg); setFg(p.fg); }}
                    className={`aspect-square rounded-lg border-2 flex items-center justify-center text-lg font-bold transition-all ${bg === p.bg && fg === p.fg ? "border-[#C9A24B]" : "border-transparent"}`}
                    style={{ background: p.bg, color: p.fg, fontFamily: fontCss }}>A</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-4">
              <div>
                <label className="text-xs text-zinc-500">Background</label>
                <input type="color" value={bg} onChange={(e) => setBg(e.target.value)}
                  className="mt-1 w-full h-10 rounded-lg bg-transparent border border-[#27272A] cursor-pointer" />
              </div>
              <div>
                <label className="text-xs text-zinc-500">Text</label>
                <input type="color" value={fg} onChange={(e) => setFg(e.target.value)}
                  className="mt-1 w-full h-10 rounded-lg bg-transparent border border-[#27272A] cursor-pointer" />
              </div>
            </div>

            <button data-testid="logo-save-text-btn" onClick={saveText} disabled={saving}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold py-3 rounded-xl disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 wt-spin" /> : <Save className="w-4 h-4" />} Save Text Logo
            </button>
          </>
        ) : (
          <div>
            <input type="file" ref={fileRef} accept="image/png,image/jpeg,image/webp"
              onChange={(e) => uploadFile(e.target.files?.[0])}
              data-testid="logo-file-input" className="hidden" />
            <button data-testid="logo-upload-btn" onClick={pickFile} disabled={saving}
              className="w-full rounded-2xl border-2 border-dashed border-[#27272A] hover:border-[#C9A24B]/50 bg-[#0A0A0A] p-8 flex flex-col items-center gap-3 transition-all">
              {uploadPreview ? (
                <img src={uploadPreview} alt="preview" className="w-24 h-24 rounded-xl object-cover" />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-[#C9A24B]/10 flex items-center justify-center">
                  <Upload className="w-8 h-8 text-[#C9A24B]" />
                </div>
              )}
              <div className="text-center">
                <div className="text-sm font-semibold">{saving ? "Uploading…" : "Tap to upload your logo"}</div>
                <div className="text-xs text-zinc-500 mt-1">PNG · JPG · WebP · max 500 KB · min 128×128px</div>
              </div>
            </button>
          </div>
        )}

        {site.logo && (
          <button data-testid="logo-remove-btn" onClick={remove} disabled={saving}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#1C1C1E] border border-[#27272A] text-sm text-zinc-300 hover:border-[#EF4444]/50 hover:text-[#EF4444]">
            <Trash2 className="w-4 h-4" /> Remove logo
          </button>
        )}
      </div>
    </div>
  );
}
